import {
  computed,
  inject,
  Injectable,
  linkedSignal,
  resource,
  Signal,
} from '@angular/core';
import { SOFTLINE_CONST_WWS_CASH_REGISTER_CONFIG_KEY_DISCOUNTS } from '../pos.const';
import { WorkstationConfigService } from '@softapps/wws/pos-core';
import { ModuleConfig } from '@softline/application';
import { numpadFn } from '@softline/ui-core/numpad';

@Injectable({ providedIn: 'root' })
export class DiscountConfigService {
  readonly workstationService = inject(WorkstationConfigService);
  readonly #numpad = numpadFn();

  readonly #localDiscountsResource = resource({
    loader: () => {
      return this.workstationService.load<{
        discounts: number[];
        generalDiscounts: number[];
      }>(SOFTLINE_CONST_WWS_CASH_REGISTER_CONFIG_KEY_DISCOUNTS);
    },
  });

  readonly localDiscounts = linkedSignal<number[]>(() => {
    return this.#localDiscountsResource.hasValue()
      ? (this.#localDiscountsResource.value()?.discounts ?? [])
      : [];
  });

  readonly localGeneralDiscounts = linkedSignal<number[]>(() => {
    return this.#localDiscountsResource.hasValue()
      ? (this.#localDiscountsResource.value()?.generalDiscounts ?? [])
      : [];
  });

  readonly hasUnsavedLocalDiscountChanges = computed(() => {
    if (this.#localDiscountsResource.hasValue()) {
      const value = this.#localDiscountsResource.value();
      const savedDiscounts = (value?.discounts ?? []).sort((a, b) => a - b);
      const savedGeneralDiscounts = (value?.generalDiscounts ?? []).sort((a, b) => a - b);
      const currentDiscounts = this.localDiscounts().sort((a, b) => a - b);
      const currentGeneralDiscounts = this.localGeneralDiscounts().sort((a, b) => a - b);

      return (
        savedDiscounts.join(',') === currentDiscounts.join(',') ||
        savedGeneralDiscounts.join(',') === currentGeneralDiscounts.join(',')
      );
    }

    return false;
  });

  readonly getDiscountTemplateValues = (
    routeData: Signal<{ config?: { modules?: ModuleConfig[] } } | undefined>
  ) => {
    return computed(() => {
      const data = routeData();
      if (!data) return null;

      // `config` is resolved only on the /pos-config route; the settings embed
      // renders this page without it — show local discounts, no remote ones.
      const posModuleConfig = data.config?.modules?.find((m) => m.module === 'pos');

      const discountsProperty = posModuleConfig?.properties.find(
        (o) => o.key === 'DISCOUNTS'
      );
      const generalDiscountsProperty = posModuleConfig?.properties.find(
        (o) => o.key === 'GENERAL_DISCOUNTS'
      );

      const savedLocal = this.#localDiscountsResource.hasValue()
        ? (this.#localDiscountsResource.value()?.discounts ?? [])
        : [];
      const savedLocalGeneral = this.#localDiscountsResource.hasValue()
        ? (this.#localDiscountsResource.value()?.generalDiscounts ?? [])
        : [];

      return {
        discountValues: (discountsProperty?.value?.split(',') ?? [])
          .map((discountValue) => ({
            value: Number(discountValue.trimStart().trimEnd()),
            source: 'remote',
            saved: true,
          }))
          .concat(
            ...this.localDiscounts().map((value) => ({
              value,
              source: 'local',
              saved: savedLocal.includes(value),
            }))
          )
          .sort((a, b) => a.value - b.value),
        generalDiscountValues: (generalDiscountsProperty?.value?.split(',') ?? [])
          .map((discountValue) => ({
            value: Number(discountValue.trimStart().trimEnd()),
            source: 'remote',
            saved: true,
          }))
          .concat(
            ...this.localGeneralDiscounts().map((value) => ({
              value,
              source: 'local',
              saved: savedLocalGeneral.includes(value),
            }))
          )
          .sort((a, b) => a.value - b.value),
      };
    });
  };

  async save(): Promise<void> {
    if (this.hasUnsavedLocalDiscountChanges()) {
      const newValue = {
        discounts: this.localDiscounts(),
        generalDiscounts: this.localGeneralDiscounts(),
      };

      await this.workstationService.save(
        SOFTLINE_CONST_WWS_CASH_REGISTER_CONFIG_KEY_DISCOUNTS,
        newValue
      );

      this.#localDiscountsResource.set(newValue);
    }
  }

  async addLocalDiscount(type: 'item' | 'document'): Promise<void> {
    const result = await this.#numpad({
      icon: 'fa-regular fa-badge-percent',
      title: 'Rabattsatz anlegen',
      subtitle: 'Neuen Vorschlagswert für die Rabatteingabe festlegen.',
      floatingPoints: 2,
      dismiss: true,
      max: 99,
      text: 'Neuen Vorschlagswert für Rabatteingabe hinzufügen',
      value: 0,
    });

    if (!result || result === 'DISMISSED') return;

    if (type === 'item') {
      this.localDiscounts.update((d) => d.concat(result));
    } else {
      this.localGeneralDiscounts.update((d) => d.concat(result));
    }
  }
}
